import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { SecondaryButton } from "@financial-edge/shared";

import api from "../api/api";
import AuthAlert from "../components/auth/AuthAlert";
import AuthContextPanel from "../components/auth/AuthContextPanel";
import AuthShell from "../components/auth/AuthShell";
import PrimaryButton from "../components/layout/PrimaryButton";
import { getSignInErrorMessage } from "../utils/auth/authDiagnostics";
import { logAuthFlowWarning } from "../utils/auth/authFlowLogger";
import { buildBlockedSignInStatusState, isBlockedSignInInterception } from "../utils/auth/interceptedAccess";
import { buildLaunchSearch, formatIntentDestinationReference, getIntentDetails, getEffectiveLaunchContext, persistLaunchContext } from "../utils/auth/intent";
import { persistPendingVerificationContext } from "../utils/auth/pendingVerification";
import { useAuthPageMeta } from "../utils/ui/useAuthPageMeta";

export default function EmailLogin() {
    const navigate = useNavigate();
    const location = useLocation();
    const launchContext = getEffectiveLaunchContext(location.search) || {};
    const intent = location.state?.intent || launchContext.intent;
    const source = location.state?.source || launchContext.source;
    const returnTo = location.state?.returnTo || launchContext.returnTo;
    const brokerageId = location.state?.brokerageId || launchContext.brokerageId;
    const phone = location.state?.phone || null;
    const methodSelection = location.state?.methodSelection || null;
    const allowEmailFallback = location.state?.allowEmailFallback === true;
    const intentDetails = getIntentDetails(intent, source) || { label: "application form" };

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    useAuthPageMeta("Email Verification");

    const emailAvailable = Boolean(methodSelection?.email?.available);
    const emailHint = methodSelection?.email?.destinationHint || "your backup email";
    const loginSearch = buildLaunchSearch({ intent, source, returnTo, brokerageId }) || location.search || "";
    const loginPath = `/login${loginSearch}`;

    useEffect(() => {
        if (allowEmailFallback && phone) {
            return;
        }

        navigate(loginPath, { replace: true });
    }, [allowEmailFallback, loginPath, navigate, phone]);

    const handleUseEmail = async (event) => {
        event?.preventDefault?.();
        setError("");

        if (!allowEmailFallback || !emailAvailable || !phone) {
            setError("Backup email verification is not available for this sign-in attempt.");
            return;
        }

        setLoading(true);

        try {
            const otpResponse = await api.requestOtp({
                phone,
                intent,
                source,
                brokerageId,
                returnTo,
                channel: "email",
            });

            persistLaunchContext({
                intent,
                source,
                returnTo,
                brokerageId,
                hasExplicitIntent: location.state?.hasExplicitIntent ?? launchContext.hasExplicitIntent,
            });
            const challengeState = {
                channel: "email",
                requestId: otpResponse?.requestId || null,
                challengeId: otpResponse?.challenge?.challengeId || null,
                phone,
                brokerageId,
                intent,
                source,
                returnTo,
                hasExplicitIntent: location.state?.hasExplicitIntent ?? launchContext.hasExplicitIntent,
                emailDestinationHint: otpResponse?.challenge?.delivery?.redactedDestination || otpResponse?.methodSelection?.email?.destinationHint || emailHint,
                methodSelection: otpResponse?.methodSelection || methodSelection,
            };
            persistPendingVerificationContext(challengeState);

            if (isBlockedSignInInterception(otpResponse?.access)) {
                navigate(`/auth-status${buildLaunchSearch({ intent, source, returnTo, brokerageId }) || ""}`, {
                    replace: true,
                    state: buildBlockedSignInStatusState(otpResponse.access, {
                        intent,
                    }),
                });
                return;
            }

            navigate(`/verify-code${buildLaunchSearch({ intent, source, returnTo, brokerageId })}`, {
                state: challengeState,
            });
        } catch (err) {
            logAuthFlowWarning("email_login_failed", {
                intent,
                source,
                message: err?.message || "unknown_error",
            });
            setError(err?.message || getSignInErrorMessage(err));
        } finally {
            setLoading(false);
        }
    };

    return (
        <AuthShell
            eyebrow="Step 2 of 3"
            title="Use your backup email"
            description={`If you cannot access SMS right now, we can send a one-time code to ${emailHint} so you can continue to ${formatIntentDestinationReference(intentDetails.label)}.`}
            footer={<p className="text-sm text-secondary">Backup email remains a fallback path only. Mobile OTP is still the primary sign-in method.</p>}
        >
            <form onSubmit={handleUseEmail} noValidate>
                <AuthContextPanel
                    title="Confirm your backup path"
                    items={[
                        {
                            label: "Workspace",
                            value: intentDetails.label,
                        },
                        {
                            label: "Backup email",
                            value: emailHint,
                        },
                    ]}
                    helper={emailAvailable
                        ? "We will send a fresh one-time code to your backup email."
                        : "Backup email verification is not available for this identity. Use recovery if you cannot access SMS."}
                />

                {error ? (
                    <div className="mt-4">
                        <AuthAlert tone="error" title="Email verification could not continue" live="assertive">
                            {error}
                        </AuthAlert>
                    </div>
                ) : null}

                <div className="mt-8 flex flex-col-reverse gap-3 sm:flex-row">
                    <SecondaryButton
                        type="button"
                        onClick={() => navigate(loginPath, { replace: true })}
                        className="flex-1 text-lg"
                        disabled={loading}
                    >
                        Back
                    </SecondaryButton>

                    <PrimaryButton
                        type="submit"
                        className="flex-1"
                        disabled={loading || !emailAvailable}
                        aria-busy={loading}
                    >
                        {loading ? "Sending secure code..." : "Send code to backup email"}
                    </PrimaryButton>
                </div>

                <div className="mt-4">
                    <button
                        type="button"
                        onClick={() => navigate("/auth/recovery", {
                            state: {
                                phone,
                                intent,
                                source,
                                returnTo,
                                email: methodSelection?.email?.destinationHint || null,
                            },
                        })}
                        className="font-semibold text-primary hover:underline focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded"
                    >
                        Need account recovery instead?
                    </button>
                </div>
            </form>
        </AuthShell>
    );
}
